/**
 * Leftover Keep Dialog (#728)
 *
 * Moves a resolved transit-warehouse leftover into real stock, letting the
 * foreman pick the destination warehouse and (optionally) a bin location —
 * previously hardcoded to the tenant's single default core warehouse with
 * no location at all.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, MenuItem, Select, FormControl, InputLabel, Typography } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import type { WarehouseItem } from '@/hooks/useInventory';

interface LeftoverKeepDialogProps {
  open: boolean;
  onClose: () => void;
  partName: string;
  quantity: number;
  warehouses: WarehouseItem[];
  onConfirm: (warehouseId: string, binLocationId: string | null) => void;
  isLoading?: boolean;
}

export function LeftoverKeepDialog({
  open,
  onClose,
  partName,
  quantity,
  warehouses,
  onConfirm,
  isLoading,
}: LeftoverKeepDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);

  // Transit warehouses aren't valid "keep" destinations — leftovers already
  // live there, so keeping them "in" transit would be a no-op.
  const destinationWarehouses = warehouses.filter((w) => w.warehouseType !== 'transit');

  const [warehouseId, setWarehouseId] = useState('');
  const [binLocationId, setBinLocationId] = useState('');

  useEffect(() => {
    if (open) {
      const defaultWarehouse = destinationWarehouses.find((w) => w.isDefault) ?? destinationWarehouses[0];
      setWarehouseId(defaultWarehouse?.id ?? '');
      setBinLocationId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedWarehouse = destinationWarehouses.find((w) => w.id === warehouseId);
  const binOptions = selectedWarehouse?.binLocations ?? [];

  const handleSubmit = () => {
    if (!warehouseId) return;
    onConfirm(warehouseId, binLocationId || null);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('transitWarehouse.keepDialog.title')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('transitWarehouse.keepDialog.confirm')}
      isLoading={isLoading}
      maxWidth="xs"
    >
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('transitWarehouse.keepDialog.description', { partName, count: quantity })}
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="leftover-keep-warehouse-label">{t('transitWarehouse.keepDialog.warehouseLabel')}</InputLabel>
          <Select
            labelId="leftover-keep-warehouse-label"
            label={t('transitWarehouse.keepDialog.warehouseLabel')}
            value={warehouseId}
            onChange={(e) => {
              setWarehouseId(e.target.value);
              setBinLocationId('');
              markDirty();
            }}
          >
            {destinationWarehouses.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={binOptions.length === 0}>
          <InputLabel id="leftover-keep-bin-label">{t('transitWarehouse.keepDialog.binLocationLabel')}</InputLabel>
          <Select
            labelId="leftover-keep-bin-label"
            label={t('transitWarehouse.keepDialog.binLocationLabel')}
            value={binLocationId}
            onChange={(e) => {
              setBinLocationId(e.target.value);
              markDirty();
            }}
          >
            <MenuItem value="">{t('transitWarehouse.keepDialog.noBinLocation')}</MenuItem>
            {binOptions.map((bin) => (
              <MenuItem key={bin.id} value={bin.id}>{bin.code}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
    </FormDialog>
  );
}
